import React, { useState, useEffect } from 'react';
import { Users, Plus, Edit, Mail, Phone, Search, X, UserPlus, RefreshCw, AlertCircle, CheckCircle } from 'lucide-react';
import { API_BASE_URL, API_ENDPOINTS, getAuthHeaders } from '../config/api.ts';
import FormSelect from '../components/FormSelect.tsx';

interface Staff {
  _id: string;
  name: string;
  email: string;
  phone: string;
  position: string;
  branch: {
    _id: string;
    name: string;
    code: string;
  };
  isActive: boolean;
}

const Personel: React.FC = () => {
  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [filteredStaffList, setFilteredStaffList] = useState<Staff[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [positionFilter, setPositionFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<Staff | null>(null);
  const [addLoading, setAddLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [activateLoading, setActivateLoading] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [addSuccess, setAddSuccess] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [editSuccess, setEditSuccess] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  
  // Form state'leri
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    password: '',
    confirmPassword: ''
  });

  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    password: '',
    confirmPassword: ''
  });

  const fetchStaffList = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('adminToken');
      if (!token) {
        throw new Error('Admin token bulunamadı');
      }

      const headers = getAuthHeaders(token);

      // Staff listesini çek (hotel admin'in kendi branch'ındaki staff'lar)
      const response = await fetch(`${API_BASE_URL}/api/staff/branch`, {
        method: 'GET',
        headers,
      });

      const data = await response.json();
      if (data.success) {
        setStaffList(data.staff);
      } else {
        throw new Error(data.message || 'Staff listesi getirilemedi');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Bilinmeyen hata');
    } finally {
      setLoading(false);
    }
  };

  // Filtreleme fonksiyonu
  const filterStaff = () => {
    let filtered = staffList;

    // Arama filtresi
    if (searchTerm) {
      filtered = filtered.filter(staff =>
        staff.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        staff.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        staff.phone.includes(searchTerm) ||
        staff.position.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Pozisyon filtresi
    if (positionFilter) {
      filtered = filtered.filter(staff => staff.position === positionFilter);
    }

    // Durum filtresi
    if (statusFilter !== 'all') {
      const isActive = statusFilter === 'active';
      filtered = filtered.filter(staff => staff.isActive === isActive);
    }

    setFilteredStaffList(filtered);
  };

  // Staff listesi değiştiğinde filtrele
  useEffect(() => {
    filterStaff();
  }, [staffList, searchTerm, positionFilter, statusFilter]);

  // Düzenleme modal'ını aç
  const handleEditClick = (staff: Staff) => {
    setSelectedStaff(staff);
    setEditFormData({
      name: staff.name,
      email: staff.email,
      phone: staff.phone,
      position: staff.position,
      password: '',
      confirmPassword: ''
    });
    setEditError(null);
    setEditSuccess(null);
    setShowEditModal(true);
  };

  // Silme modal'ını aç
  const handleDeleteClick = (staff: Staff) => {
    setSelectedStaff(staff);
    setConfirmDelete(false);
    setShowDeleteModal(true);
  };

  // Form işleme fonksiyonları
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      position: '',
      password: '',
      confirmPassword: ''
    });
    setAddError(null);
    setAddSuccess(null);
  };

  // Güçlü şifre validasyonu
  const validatePassword = (password: string) => {
    const errors = [];
    
    if (password.length < 8) {
      errors.push('En az 8 karakter');
    }
    
    if (!/[A-Z]/.test(password)) {
      errors.push('En az 1 büyük harf');
    }
    
    if (!/[a-z]/.test(password)) {
      errors.push('En az 1 küçük harf');
    }
    
    if (!/[0-9]/.test(password)) {
      errors.push('En az 1 rakam');
    }
    
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      errors.push('En az 1 özel karakter');
    }
    
    return errors;
  };

  // Şifre güçlülük kontrolü
  const getPasswordStrength = (password: string) => {
    const errors = validatePassword(password);
    const strength = 5 - errors.length;
    return {
      strength,
      errors,
      isValid: errors.length === 0
    };
  };

  // Şifre eşleşme kontrolü
  const isPasswordMatch = (password: string, confirmPassword: string) => {
    return password && confirmPassword && password === confirmPassword;
  };

  const handleAddStaff = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validasyon
    if (!formData.name || !formData.email || !formData.phone || !formData.position || !formData.password) {
      setAddError('Tüm alanları doldurun');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setAddError('Şifreler eşleşmiyor');
      return;
    }

    // Güçlü şifre validasyonu
    const passwordErrors = validatePassword(formData.password);
    if (passwordErrors.length > 0) {
      setAddError(`Şifre güvenlik gereksinimleri: ${passwordErrors.join(', ')}`);
      return;
    }

    // Email format kontrolü
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setAddError('Geçerli bir email adresi girin');
      return;
    }

    // Telefon format kontrolü
    const phoneRegex = /^[0-9+\-\s()]+$/;
    if (!phoneRegex.test(formData.phone)) {
      setAddError('Geçerli bir telefon numarası girin');
      return;
    }

    try {
      setAddLoading(true);
      setAddError(null);

      const token = localStorage.getItem('adminToken');
      if (!token) {
        throw new Error('Admin token bulunamadı');
      }

      const headers = getAuthHeaders(token);

      const response = await fetch(`${API_BASE_URL}/api/staff/add`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.toLowerCase().trim(),
          phone: formData.phone.trim(),
          position: formData.position.trim(),
          password: formData.password
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setAddSuccess('Personel başarıyla eklendi!');
        resetForm();
        setShowAddModal(false);
        fetchStaffList(); // Listeyi yenile
      } else {
        setAddError(data.message || 'Personel eklenirken hata oluştu');
      }
    } catch (error) {
      setAddError(error instanceof Error ? error.message : 'Bilinmeyen hata');
    } finally {
      setAddLoading(false);
    }
  };

  // Düzenleme işlemi
  const handleEditStaff = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedStaff) return;

    // Validasyon
    if (!editFormData.name || !editFormData.email || !editFormData.phone || !editFormData.position) {
      setEditError('Tüm zorunlu alanları doldurun');
      return;
    }

    // Email format kontrolü
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editFormData.email)) {
      setEditError('Geçerli bir email adresi girin');
      return;
    }

    // Telefon format kontrolü
    const phoneRegex = /^[0-9+\-\s()]+$/;
    if (!phoneRegex.test(editFormData.phone)) {
      setEditError('Geçerli bir telefon numarası girin');
      return;
    }

    // Şifre kontrolü (eğer girilmişse)
    if (editFormData.password && editFormData.password !== editFormData.confirmPassword) {
      setEditError('Şifreler eşleşmiyor');
      return;
    }

    // Güçlü şifre validasyonu (eğer şifre girilmişse)
    if (editFormData.password) {
      const passwordErrors = validatePassword(editFormData.password);
      if (passwordErrors.length > 0) {
        setEditError(`Şifre güvenlik gereksinimleri: ${passwordErrors.join(', ')}`);
        return;
      }
    }

    try {
      setEditLoading(true);
      setEditError(null);

      const token = localStorage.getItem('adminToken');
      if (!token) {
        throw new Error('Admin token bulunamadı');
      }

      const headers = getAuthHeaders(token);

      const updateData: any = {
        name: editFormData.name.trim(),
        email: editFormData.email.toLowerCase().trim(),
        phone: editFormData.phone.trim(),
        position: editFormData.position.trim()
      };

      // Şifre sadece girilmişse ekle
      if (editFormData.password) {
        updateData.password = editFormData.password;
      }

      const response = await fetch(`${API_BASE_URL}/api/staff/${selectedStaff._id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(updateData)
      });

      const data = await response.json();
      
      if (data.success) {
        setEditSuccess('Personel başarıyla güncellendi!');
        setShowEditModal(false);
        fetchStaffList(); // Listeyi yenile
      } else {
        setEditError(data.message || 'Personel güncellenirken hata oluştu');
      }
    } catch (error) {
      setEditError(error instanceof Error ? error.message : 'Bilinmeyen hata');
    } finally {
      setEditLoading(false);
    }
  };

  // Aktif yapma işlemi
  const handleActivateStaff = async () => {
    if (!selectedStaff) return;

    try {
      setActivateLoading(true);

      const token = localStorage.getItem('adminToken');
      if (!token) {
        throw new Error('Admin token bulunamadı');
      }

      const headers = getAuthHeaders(token);

      const response = await fetch(`${API_BASE_URL}/api/staff/${selectedStaff._id}/activate`, {
        method: 'PUT',
        headers
      });

      const data = await response.json();
      
      if (data.success) {
        setShowActivateModal(false);
        fetchStaffList(); // Listeyi yenile
        alert('✅ Personel başarıyla aktif yapıldı');
      } else {
        alert(`❌ Hata: ${data.message || 'Personel aktif yapılırken hata oluştu'}`);
      }
    } catch (error) {
      console.error('Activate staff error:', error);
      alert('❌ Personel aktif yapılırken hata oluştu');
    } finally {
      setActivateLoading(false);
    }
  };

  // Pasif yapma işlemi
  const handleDeleteStaff = async () => {
    if (!selectedStaff) return;

    try {
      setDeleteLoading(true);

      const token = localStorage.getItem('adminToken');
      if (!token) {
        throw new Error('Admin token bulunamadı');
      }

      const headers = getAuthHeaders(token);

      const response = await fetch(`${API_BASE_URL}/api/staff/${selectedStaff._id}/deactivate`, {
        method: 'PUT',
        headers
      });

      const data = await response.json();
      
      if (data.success) {
        setShowDeleteModal(false);
        fetchStaffList(); // Listeyi yenile
        alert('✅ Personel başarıyla pasif yapıldı');
      } else {
        alert(`❌ Hata: ${data.message || 'Personel pasif yapılırken hata oluştu'}`);
      }
    } catch (error) {
      console.error('Deactivate staff error:', error);
      alert('❌ Personel pasif yapılırken hata oluştu');
    } finally {
      setDeleteLoading(false);
    }
  };

  useEffect(() => {
    fetchStaffList();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-stone-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Şube Personel Yönetimi</h1>
          <p className="text-gray-600 mt-1">
            Şubenizdeki personelleri yönetin 
            {!loading && (
              <span className="ml-2 text-blue-500 font-semibold">
                ({filteredStaffList.length} / {staffList.length} personel)
              </span>
            )}
          </p>
        </div>
        <div className="flex space-x-3 mt-4 sm:mt-0">
          <button 
            onClick={fetchStaffList}
            className="bg-white border-2 border-gray-300 text-gray-700 px-5 py-2.5 rounded-xl hover:border-blue-300 hover:bg-blue-50 transition-all flex items-center font-semibold shadow-sm"
          >
            <RefreshCw size={20} className="mr-2" />
            Yenile
          </button>
          
          <button 
            onClick={() => setShowAddModal(true)}
            className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-5 py-2.5 rounded-xl hover:from-blue-600 hover:to-cyan-600 transition-all flex items-center font-semibold shadow-md"
          >
            <UserPlus size={20} className="mr-2" />
            Personel Ekle
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-5">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-red-400 to-red-500 rounded-xl flex items-center justify-center">
              <AlertCircle size={20} className="text-white" />
            </div>
            <span className="text-red-800 font-bold">{error}</span>
          </div>
        </div>
      )}

      {/* Search and Filter Section */}
      <div className="bg-white rounded-xl shadow-sm border-2 border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Arama
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search size={20} className="text-blue-400" />
              </div>
              <input
                type="text"
                placeholder="Ad, email, telefon..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-transparent transition-all"
              />
            </div>
          </div>

          <FormSelect
            label="Pozisyon"
            value={positionFilter}
            onChange={(e) => setPositionFilter(e.target.value)}
          >
            <option value="">Tümü</option>
            <option value="hotel_staff">Hotel Staff</option>
            <option value="manager">Manager</option>
            <option value="receptionist">Receptionist</option>
            <option value="caretaker">Caretaker</option>
          </FormSelect>

          <FormSelect
            label="Durum"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">Tümü</option>
            <option value="active">Aktif</option>
            <option value="inactive">Pasif</option>
          </FormSelect>

          {/* Clear Filters */}
          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setPositionFilter('');
                setStatusFilter('all');
              }}
              className="w-full bg-white border-2 border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all font-semibold"
            >
              Filtreleri Temizle
            </button>
          </div>
        </div>
      </div>

      {/* Staff Table */}
      <div className="bg-white rounded-xl shadow-md border-2 border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Personel listesi yükleniyor...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-blue-50 to-cyan-50 border-b-2 border-blue-100">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Personel
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    İletişim
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Şube
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Durum
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    İşlemler
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {filteredStaffList.map((staff) => (
                  <tr key={staff._id} className="hover:bg-blue-50/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-cyan-400 rounded-full flex items-center justify-center text-white font-bold shadow-sm">
                          {staff.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{staff.name}</div>
                          <div className="text-sm text-gray-500">{staff.position}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        <div className="flex items-center text-sm text-gray-600">
                          <div className="mr-2 text-gray-500">
                            <Mail size={14} />
                          </div>
                          {staff.email}
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <div className="mr-2 text-gray-500">
                            <Phone size={14} />
                          </div>
                          {staff.phone}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{staff.branch?.name || 'Belirtilmemiş'}</div>
                      <div className="text-sm text-gray-500">{staff.branch?.code || ''}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        staff.isActive 
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {staff.isActive ? 'Aktif' : 'Pasif'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button 
                          onClick={() => handleEditClick(staff)}
                          className="w-8 h-8 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg transition-all flex items-center justify-center"
                          title="Düzenle"
                        >
                          <Edit size={16} />
                        </button>
                        {staff.isActive ? (
                          <button 
                            onClick={() => handleDeleteClick(staff)}
                            className="w-8 h-8 bg-orange-100 hover:bg-orange-200 text-orange-600 rounded-lg transition-all flex items-center justify-center"
                            title="Pasif Yap"
                          >
                            <AlertCircle size={16} />
                          </button>
                        ) : (
                          <button 
                            onClick={() => {
                              setSelectedStaff(staff);
                              setShowActivateModal(true);
                            }}
                            className="w-8 h-8 bg-green-100 hover:bg-green-200 text-green-600 rounded-lg transition-all flex items-center justify-center"
                            title="Aktif Yap"
                          >
                            <CheckCircle size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* No Staff Message */}
      {!loading && filteredStaffList.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm border-2 border-gray-200 p-12 text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Users size={32} className="text-blue-500" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">
            {staffList.length === 0 ? 'Personel Bulunamadı' : 'Filtreleme Sonucu Bulunamadı'}
          </h3>
          <p className="text-gray-600">
            {staffList.length === 0 
              ? 'Bu şubede kayıtlı personel bulunmuyor.' 
              : 'Seçilen kriterlere uygun personel bulunamadı.'
            }
          </p>
        </div>
      )}

      {/* Add Staff Modal */}
      {showAddModal && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          style={{ top: '-30px' }}
        >
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-hidden shadow-2xl">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-500 to-cyan-500 p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mr-3">
                    <UserPlus size={20} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white">Yeni Personel Ekle</h3>
                </div>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                  className="text-white/70 hover:text-white hover:bg-white/10 p-2 rounded-xl transition-all"
                >
                  <X size={22} />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleAddStaff} className="p-6 space-y-4 overflow-y-auto max-h-[calc(90vh-80px)]">
              {/* Success Message */}
              {addSuccess && (
                <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-md">
                  {addSuccess}
                </div>
              )}

              {/* Error Message */}
              {addError && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
                  {addError}
                </div>
              )}

              {/* Name */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                  Ad Soyad <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Personel adı ve soyadı"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-all bg-gray-50 focus:bg-white font-medium"
                  required
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                  Email <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="personel@email.com"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-all bg-gray-50 focus:bg-white font-medium"
                  required
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                  Telefon <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="0555 123 45 67"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-all bg-gray-50 focus:bg-white font-medium"
                  required
                />
              </div>

              {/* Position */}
              <FormSelect
                name="position"
                value={formData.position}
                onChange={handleInputChange}
                required
                label={
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-blue-500 rounded-full inline-block"></span>
                    Pozisyon <span className="text-red-500">*</span>
                  </span>
                }
                labelClassName=""
              >
                <option value="">Pozisyon seçin</option>
                <option value="hotel_staff">Hotel Staff</option>
                <option value="manager">Manager</option>
                <option value="receptionist">Receptionist</option>
                <option value="caretaker">Caretaker</option>
              </FormSelect>

              {/* Password */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                  Şifre <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="En az 8 karakter, büyük/küçük harf, rakam ve özel karakter"
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-colors font-medium ${
                    formData.password 
                      ? getPasswordStrength(formData.password).isValid 
                        ? 'border-green-500 focus:ring-green-500 bg-green-50' 
                        : 'border-red-500 focus:ring-red-500 bg-red-50'
                      : 'border-gray-300 focus:ring-blue-400 bg-gray-50 focus:bg-white'
                  }`}
                  required
                />
                
                {/* Şifre Güçlülük Göstergesi */}
                {formData.password && (
                  <div className="mt-2">
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full transition-all duration-300 ${
                            getPasswordStrength(formData.password).strength <= 1 ? 'bg-red-500' :
                            getPasswordStrength(formData.password).strength <= 2 ? 'bg-orange-500' :
                            getPasswordStrength(formData.password).strength <= 3 ? 'bg-yellow-500' :
                            getPasswordStrength(formData.password).strength <= 4 ? 'bg-blue-500' :
                            'bg-green-500'
                          }`}
                          style={{ 
                            width: `${(getPasswordStrength(formData.password).strength / 5) * 100}%` 
                          }}
                        ></div>
                      </div>
                      <span className={`text-xs font-medium ${
                        getPasswordStrength(formData.password).isValid ? 'text-green-600' : 'text-gray-600'
                      }`}>
                        {getPasswordStrength(formData.password).isValid ? '✓ Güçlü' : 'Zayıf'}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-1 text-xs">
                      <div className={`flex items-center ${formData.password.length >= 8 ? 'text-green-600' : 'text-gray-500'}`}>
                        <span className="mr-1">{formData.password.length >= 8 ? '✓' : '○'}</span>
                        En az 8 karakter
                      </div>
                      <div className={`flex items-center ${/[A-Z]/.test(formData.password) ? 'text-green-600' : 'text-gray-500'}`}>
                        <span className="mr-1">{/[A-Z]/.test(formData.password) ? '✓' : '○'}</span>
                        Büyük harf (A-Z)
                      </div>
                      <div className={`flex items-center ${/[a-z]/.test(formData.password) ? 'text-green-600' : 'text-gray-500'}`}>
                        <span className="mr-1">{/[a-z]/.test(formData.password) ? '✓' : '○'}</span>
                        Küçük harf (a-z)
                      </div>
                      <div className={`flex items-center ${/[0-9]/.test(formData.password) ? 'text-green-600' : 'text-gray-500'}`}>
                        <span className="mr-1">{/[0-9]/.test(formData.password) ? '✓' : '○'}</span>
                        Rakam (0-9)
                      </div>
                      <div className={`flex items-center col-span-2 ${/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(formData.password) ? 'text-green-600' : 'text-gray-500'}`}>
                        <span className="mr-1">{/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(formData.password) ? '✓' : '○'}</span>
                        Özel karakter (!@#$%^&*)
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                  Şifre Tekrar <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="Şifreyi tekrar girin"
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-colors font-medium ${
                    formData.confirmPassword 
                      ? isPasswordMatch(formData.password, formData.confirmPassword)
                        ? 'border-green-500 focus:ring-green-500 bg-green-50' 
                        : 'border-red-500 focus:ring-red-500 bg-red-50'
                      : 'border-gray-300 focus:ring-blue-400 bg-gray-50 focus:bg-white'
                  }`}
                  required
                />
                
                {/* Şifre Eşleşme Göstergesi */}
                {formData.confirmPassword && (
                  <div className="mt-1 flex items-center text-xs">
                    {isPasswordMatch(formData.password, formData.confirmPassword) ? (
                      <span className="text-green-600 flex items-center">
                        <span className="mr-1">✓</span>
                        Şifreler eşleşiyor
                      </span>
                    ) : (
                      <span className="text-red-600 flex items-center">
                        <span className="mr-1">✗</span>
                        Şifreler eşleşmiyor
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Branch Info */}
              <div className="bg-blue-50 border-2 border-blue-200 p-4 rounded-xl">
                <p className="text-sm text-blue-800 font-medium">
                  <strong>Not:</strong> Personel otomatik olarak sizin şubenize atanacaktır.
                </p>
              </div>

              {/* Modal Footer */}
              <div className="flex space-x-3 pt-4 border-t-2 border-blue-100 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                  className="flex-1 px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all font-bold"
                  disabled={addLoading}
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={addLoading}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-xl hover:from-blue-600 hover:to-cyan-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-bold shadow-lg"
                >
                  {addLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Ekleniyor...
                    </>
                  ) : (
                    'Personel Ekle'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Staff Modal */}
      {showEditModal && selectedStaff && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          style={{ top: '-30px' }}
        >
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-hidden shadow-2xl">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-500 to-cyan-500 p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mr-3">
                    <Edit size={20} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white">Personel Düzenle</h3>
                </div>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-white/70 hover:text-white hover:bg-white/10 p-2 rounded-xl transition-all"
                >
                  <X size={22} />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleEditStaff} className="p-6 space-y-4 overflow-y-auto max-h-[calc(90vh-80px)]">
              {/* Success Message */}
              {editSuccess && (
                <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-md">
                  {editSuccess}
                </div>
              )}

              {/* Error Message */}
              {editError && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
                  {editError}
                </div>
              )}

              {/* Name */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                  Ad Soyad <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={editFormData.name}
                  onChange={handleEditInputChange}
                  placeholder="Personel adı ve soyadı"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-all bg-gray-50 focus:bg-white font-medium"
                  required
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                  Email <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={editFormData.email}
                  onChange={handleEditInputChange}
                  placeholder="personel@email.com"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-all bg-gray-50 focus:bg-white font-medium"
                  required
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                  Telefon <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={editFormData.phone}
                  onChange={handleEditInputChange}
                  placeholder="0555 123 45 67"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-all bg-gray-50 focus:bg-white font-medium"
                  required
                />
              </div>

              {/* Position */}
              <FormSelect
                name="position"
                value={editFormData.position}
                onChange={handleEditInputChange}
                required
                label={
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-blue-500 rounded-full inline-block"></span>
                    Pozisyon <span className="text-red-500">*</span>
                  </span>
                }
                labelClassName=""
              >
                <option value="">Pozisyon seçin</option>
                <option value="hotel_staff">Hotel Staff</option>
                <option value="manager">Manager</option>
                <option value="receptionist">Receptionist</option>
                <option value="caretaker">Caretaker</option>
                <option value="cleaner">Cleaner</option>
                <option value="trainer">Trainer</option>
              </FormSelect>

              {/* Password (Optional) */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                  Yeni Şifre <span className="text-gray-500 ml-1 font-normal">(Opsiyonel)</span>
                </label>
                <input
                  type="password"
                  name="password"
                  value={editFormData.password}
                  onChange={handleEditInputChange}
                  placeholder="Değiştirmek istemiyorsanız boş bırakın"
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-colors font-medium ${
                    editFormData.password 
                      ? getPasswordStrength(editFormData.password).isValid 
                        ? 'border-green-500 focus:ring-green-500 bg-green-50' 
                        : 'border-red-500 focus:ring-red-500 bg-red-50'
                      : 'border-gray-300 focus:ring-blue-400 bg-gray-50 focus:bg-white'
                  }`}
                />
                
                {/* Şifre Güçlülük Göstergesi */}
                {editFormData.password && (
                  <div className="mt-2">
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full transition-all duration-300 ${
                            getPasswordStrength(editFormData.password).strength <= 1 ? 'bg-red-500' :
                            getPasswordStrength(editFormData.password).strength <= 2 ? 'bg-orange-500' :
                            getPasswordStrength(editFormData.password).strength <= 3 ? 'bg-yellow-500' :
                            getPasswordStrength(editFormData.password).strength <= 4 ? 'bg-blue-500' :
                            'bg-green-500'
                          }`}
                          style={{ 
                            width: `${(getPasswordStrength(editFormData.password).strength / 5) * 100}%` 
                          }}
                        ></div>
                      </div>
                      <span className={`text-xs font-medium ${
                        getPasswordStrength(editFormData.password).isValid ? 'text-green-600' : 'text-gray-600'
                      }`}>
                        {getPasswordStrength(editFormData.password).isValid ? '✓ Güçlü' : 'Zayıf'}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-1 text-xs">
                      <div className={`flex items-center ${editFormData.password.length >= 8 ? 'text-green-600' : 'text-gray-500'}`}>
                        <span className="mr-1">{editFormData.password.length >= 8 ? '✓' : '○'}</span>
                        En az 8 karakter
                      </div>
                      <div className={`flex items-center ${/[A-Z]/.test(editFormData.password) ? 'text-green-600' : 'text-gray-500'}`}>
                        <span className="mr-1">{/[A-Z]/.test(editFormData.password) ? '✓' : '○'}</span>
                        Büyük harf (A-Z)
                      </div>
                      <div className={`flex items-center ${/[a-z]/.test(editFormData.password) ? 'text-green-600' : 'text-gray-500'}`}>
                        <span className="mr-1">{/[a-z]/.test(editFormData.password) ? '✓' : '○'}</span>
                        Küçük harf (a-z)
                      </div>
                      <div className={`flex items-center ${/[0-9]/.test(editFormData.password) ? 'text-green-600' : 'text-gray-500'}`}>
                        <span className="mr-1">{/[0-9]/.test(editFormData.password) ? '✓' : '○'}</span>
                        Rakam (0-9)
                      </div>
                      <div className={`flex items-center col-span-2 ${/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(editFormData.password) ? 'text-green-600' : 'text-gray-500'}`}>
                        <span className="mr-1">{/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(editFormData.password) ? '✓' : '○'}</span>
                        Özel karakter (!@#$%^&*)
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Confirm Password (Optional) */}
              {editFormData.password && (
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                    Yeni Şifre Tekrar <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={editFormData.confirmPassword}
                    onChange={handleEditInputChange}
                    placeholder="Şifreyi tekrar girin"
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-colors font-medium ${
                      editFormData.confirmPassword 
                        ? isPasswordMatch(editFormData.password, editFormData.confirmPassword)
                          ? 'border-green-500 focus:ring-green-500 bg-green-50' 
                          : 'border-red-500 focus:ring-red-500 bg-red-50'
                        : 'border-gray-300 focus:ring-blue-400 bg-gray-50 focus:bg-white'
                    }`}
                    required
                  />
                  
                  {/* Şifre Eşleşme Göstergesi */}
                  {editFormData.confirmPassword && (
                    <div className="mt-1 flex items-center text-xs">
                      {isPasswordMatch(editFormData.password, editFormData.confirmPassword) ? (
                        <span className="text-green-600 flex items-center">
                          <span className="mr-1">✓</span>
                          Şifreler eşleşiyor
                        </span>
                      ) : (
                        <span className="text-red-600 flex items-center">
                          <span className="mr-1">✗</span>
                          Şifreler eşleşmiyor
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Modal Footer */}
              <div className="flex space-x-3 pt-4 border-t-2 border-blue-100 mt-6">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all font-bold"
                  disabled={editLoading}
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={editLoading}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-xl hover:from-blue-600 hover:to-cyan-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-bold shadow-lg"
                >
                  {editLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Güncelleniyor...
                    </>
                  ) : (
                    'Güncelle'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Deactivate Staff Modal */}
      {showDeleteModal && selectedStaff && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          style={{ top: '-30px' }}
        >
          <div className="bg-white rounded-2xl max-w-md w-full mx-4 shadow-2xl overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-orange-500 to-red-500 p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mr-4">
                    <AlertCircle size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Personeli Pasif Yap</h3>
                    <p className="text-sm text-orange-100">Bu işlem geri alınabilir</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="text-white/70 hover:text-white hover:bg-white/10 p-2 rounded-xl transition-all"
                >
                  <X size={22} />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              {/* Personel Bilgileri */}
              <div className="bg-white border-2 border-blue-100 rounded-xl p-5 mb-6 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-cyan-400 rounded-full flex items-center justify-center text-white font-bold mr-3 shadow-md">
                    {selectedStaff.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">{selectedStaff.name}</h4>
                    <p className="text-sm text-gray-600 font-medium">{selectedStaff.position}</p>
                  </div>
                </div>
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center">
                    <Mail size={14} />
                    <span className="ml-2">{selectedStaff.email}</span>
                  </div>
                  <div className="flex items-center">
                    <Phone size={14} />
                    <span className="ml-2">{selectedStaff.phone}</span>
                  </div>
                  <div className="flex items-center">
                    <Users size={14} />
                    <span className="ml-2">{selectedStaff.branch?.name || 'Şube bilgisi yok'}</span>
                  </div>
                </div>
              </div>

              {/* Bilgi Mesajı */}
              <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-5 mb-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-red-400 rounded-xl flex items-center justify-center">
                      <AlertCircle className="h-5 w-5 text-white" />
                    </div>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-bold text-orange-900">
                      Personel Pasif Yapılacak
                    </h3>
                    <div className="mt-2 text-sm text-orange-800 font-medium">
                      <ul className="list-disc list-inside space-y-1">
                        <li>Personel sisteme giriş yapamayacak</li>
                        <li>Rezervasyon ve işlem yapamayacak</li>
                        <li>Tüm veriler korunacak</li>
                        <li>İstediğiniz zaman tekrar aktif yapabilirsiniz</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Onay Checkbox */}
              <div className="flex items-start mb-6">
                <div className="flex items-center h-5">
                  <input
                    id="confirm-deactivate"
                    type="checkbox"
                    checked={confirmDelete}
                    onChange={(e) => setConfirmDelete(e.target.checked)}
                    className="w-4 h-4 text-orange-600 bg-gray-100 border-gray-300 rounded focus:ring-orange-500 focus:ring-2"
                  />
                </div>
                <label htmlFor="confirm-deactivate" className="ml-2 text-sm text-gray-700">
                  <strong>{selectedStaff.name}</strong> adlı personeli pasif yapmak istediğimi onaylıyorum
                </label>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t-2 border-orange-100">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all font-bold"
                disabled={deleteLoading}
              >
                İptal
              </button>
              <button
                onClick={handleDeleteStaff}
                disabled={deleteLoading || !confirmDelete}
                className="px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl hover:from-orange-600 hover:to-red-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center font-bold shadow-lg"
              >
                {deleteLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Pasif Yapılıyor...
                  </>
                ) : (
                  <>
                    <AlertCircle size={16} className="mr-2" />
                    Pasif Yap
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Activate Staff Modal */}
      {showActivateModal && selectedStaff && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          style={{ top: '-30px' }}
        >
          <div className="bg-white rounded-2xl max-w-md w-full mx-4 shadow-2xl overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-green-500 to-emerald-500 p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mr-4">
                    <CheckCircle size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Personeli Aktif Yap</h3>
                    <p className="text-sm text-green-100">Personel tekrar sisteme giriş yapabilecek</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowActivateModal(false)}
                  className="text-white/70 hover:text-white hover:bg-white/10 p-2 rounded-xl transition-all"
                >
                  <X size={22} />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              {/* Personel Bilgileri */}
              <div className="bg-white border-2 border-blue-100 rounded-xl p-5 mb-6 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-cyan-400 rounded-full flex items-center justify-center text-white font-bold mr-3 shadow-md">
                    {selectedStaff.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">{selectedStaff.name}</h4>
                    <p className="text-sm text-gray-600 font-medium">{selectedStaff.position}</p>
                  </div>
                </div>
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center">
                    <Mail size={14} />
                    <span className="ml-2">{selectedStaff.email}</span>
                  </div>
                  <div className="flex items-center">
                    <Phone size={14} />
                    <span className="ml-2">{selectedStaff.phone}</span>
                  </div>
                  <div className="flex items-center">
                    <Users size={14} />
                    <span className="ml-2">{selectedStaff.branch?.name || 'Şube bilgisi yok'}</span>
                  </div>
                </div>
              </div>

              {/* Bilgi Mesajı */}
              <div className="bg-green-50 border-2 border-green-200 rounded-xl p-5 mb-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-emerald-400 rounded-xl flex items-center justify-center">
                      <CheckCircle className="h-5 w-5 text-white" />
                    </div>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-bold text-green-900">
                      Personel Aktif Yapılacak
                    </h3>
                    <div className="mt-2 text-sm text-green-800 font-medium">
                      <ul className="list-disc list-inside space-y-1">
                        <li>Personel sisteme giriş yapabilecek</li>
                        <li>Rezervasyon ve işlem yapabilecek</li>
                        <li>Tüm yetkileri geri gelecek</li>
                        <li>Hemen kullanıma hazır olacak</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t-2 border-green-100">
              <button
                onClick={() => setShowActivateModal(false)}
                className="px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all font-bold"
                disabled={activateLoading}
              >
                İptal
              </button>
              <button
                onClick={handleActivateStaff}
                disabled={activateLoading}
                className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl hover:from-green-600 hover:to-emerald-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center font-bold shadow-lg"
              >
                {activateLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Aktif Yapılıyor...
                  </>
                ) : (
                  <>
                    <CheckCircle size={16} className="mr-2" />
                    Aktif Yap
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default Personel;